import { Button, cn, ErrorState, Skeleton, Tooltip } from '@portal/ui';
import { useLocalize } from '@portal/web-sdk';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandMark, useBranding } from './branding';
import { webModules } from '../modules';
import { NavIcon } from './icons';
import { findTrail, nodesById, useFavorites, useNavigation, type NavNode } from './navigation';

/**
 * Path of the menu entry for the current page: the longest one that prefixes it, so a detail page
 * (`/youtube/channels/…`) lights up "Kênh" only, not also its parent section (`/youtube`).
 */
function useActivePath(): string | undefined {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigation();
  return findTrail(nav.data?.items ?? [], pathname).at(-1)?.path;
}

const isActive = (path: string | undefined, activePath: string | undefined) =>
  path !== undefined && path === activePath;

/** Count hooks of menu entries, by navigation id (web modules' `navBadges`). */
const badges: Record<string, () => number> = Object.assign(
  {},
  ...webModules.map((m) => m.navBadges ?? {}),
);

/** A menu entry's count; `dot` on the collapsed rail. */
function NavBadge({ use, dot }: { use: () => number; dot?: boolean }) {
  const count = use();
  if (count <= 0) return null;
  return dot ? (
    <span className="absolute top-1 right-1 size-2 rounded-full bg-primary" aria-hidden />
  ) : (
    <span className="ml-auto rounded-full bg-primary px-1.5 text-[10px] leading-4 font-medium text-primary-foreground tabular-nums">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
  /** Called after a link is followed (closes the mobile drawer). */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const brand = useBranding();
  const nav = useNavigation();

  return (
    // Always dark (brand charcoal): `.dark` switches the tokens of everything inside.
    <div className="dark flex h-full flex-col bg-sidebar text-sidebar-foreground [color-scheme:dark]">
      <div
        className={cn(
          'flex h-12 shrink-0 items-center gap-2 px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        <Link
          to="/"
          onClick={onNavigate}
          className="flex min-w-0 items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <BrandMark size="sm" />
          {collapsed ? null : (
            <span className="truncate text-sm font-semibold text-foreground">{brand.name}</span>
          )}
        </Link>
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-1">
        {nav.isPending ? (
          <div className="space-y-1.5 px-1 pt-1">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-7" />
            ))}
          </div>
        ) : nav.isError ? (
          collapsed ? null : (
            <ErrorState
              className="px-3 py-6"
              title={t('shell.navLoadError')}
              retryLabel={t('shell.retry')}
              onRetry={() => void nav.refetch()}
            />
          )
        ) : (
          <ul className="space-y-0.5">
            <FavoritesSection
              items={nav.data.items}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
            {nav.data.items.map((item) => (
              <NavEntry key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </ul>
        )}
      </nav>

      {onToggleCollapsed ? (
        <div
          className={cn(
            'shrink-0 border-t border-sidebar-border p-2',
            collapsed && 'flex justify-center',
          )}
        >
          <Tooltip
            content={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            side="right"
          >
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            >
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </Button>
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}

/** The current page: brand orange text on a faint orange wash, with a bar on the left edge. */
const activeClass =
  'bg-primary/15 text-primary hover:bg-primary/20 before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-full before:bg-primary';

const itemClass =
  'relative flex h-7 w-full items-center gap-2 rounded-md px-2 text-sm transition-colors outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/60';

function NavEntry({
  item,
  collapsed,
  onNavigate,
  depth = 0,
}: {
  item: NavNode;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
  depth?: number;
}) {
  const localize = useLocalize();
  const activePath = useActivePath();
  const children = item.children ?? [];
  const containsActive = children.some((c) => isActive(c.path, activePath));
  const [open, setOpen] = useState(true);
  const label = localize(item.label);

  // Collapsed rail: groups flatten into their children so every page stays one click away.
  if (collapsed) {
    const leaves = item.path ? [item] : children;
    return (
      <>
        {leaves.map((leaf) => {
          return (
            <li key={leaf.id}>
              <Tooltip content={localize(leaf.label)} side="right">
                <Link
                  to={leaf.path!}
                  onClick={onNavigate}
                  aria-label={localize(leaf.label)}
                  className={cn(
                    itemClass,
                    'relative justify-center px-0',
                    isActive(leaf.path, activePath) && activeClass,
                  )}
                >
                  <NavIcon name={leaf.icon} className="size-4" />
                  {badges[leaf.id] ? <NavBadge use={badges[leaf.id]!} dot /> : null}
                </Link>
              </Tooltip>
            </li>
          );
        })}
      </>
    );
  }

  if (children.length > 0 && !item.path) {
    return (
      <li className="pt-3 first:pt-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="group flex h-6 w-full items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          {label}
          <ChevronDown
            className={cn(
              'size-3 transition-transform',
              !open && '-rotate-90',
              containsActive && !open && 'text-primary',
            )}
          />
        </button>
        {open ? (
          <ul className="mt-0.5 space-y-0.5">
            {children.map((child) => (
              <NavEntry
                key={child.id}
                item={child}
                collapsed={false}
                onNavigate={onNavigate}
                depth={depth + 1}
              />
            ))}
          </ul>
        ) : null}
      </li>
    );
  }

  return (
    <li>
      <Link
        to={item.path!}
        onClick={onNavigate}
        className={cn(itemClass, isActive(item.path, activePath) && cn(activeClass, 'font-medium'))}
      >
        <NavIcon
          name={item.icon}
          className={cn(
            'size-4 shrink-0',
            isActive(item.path, activePath) ? 'text-primary' : 'text-muted-foreground',
          )}
        />
        <span className="truncate">{label}</span>
        {badges[item.id] ? <NavBadge use={badges[item.id]!} /> : null}
      </Link>
    </li>
  );
}

/** Pinned pages at the top of the menu (only those the user can still reach). */
function FavoritesSection({
  items,
  collapsed,
  onNavigate,
}: {
  items: NavNode[];
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { navIds } = useFavorites();
  const byId = nodesById(items);
  const pinned = navIds.flatMap((id) => {
    const node = byId.get(id);
    return node ? [{ ...node, children: undefined }] : [];
  });
  if (pinned.length === 0) return null;
  const group: NavNode = {
    id: 'favorites',
    label: { vi: t('shell.favorites'), en: t('shell.favorites') },
    children: pinned,
  };
  return <NavEntry item={group} collapsed={collapsed} onNavigate={onNavigate} />;
}
